import React, { useEffect, useMemo, useState } from 'react'
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell, LineChart, Line,
} from 'recharts'
import {
  LayoutDashboard, Sun, CloudSun, CloudLightning, ThumbsUp, Lightbulb, MessageSquare,
  Activity, Zap, Target, ShieldAlert, ArrowRight, Cpu,
} from 'lucide-react'
import { RunStats, TimelinePoint, CampaignStats, CampaignSummary, SysInfo, HourlyPoint, HourlyCampaignPoint, ideasList, Idea, getIdentifierStats, IdentifierStats, getProfilePoolHistory, PoolHistory } from '../api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface Props {
  stats: RunStats
  timeline: TimelinePoint[]
  hourlyHistory: HourlyPoint[]
  hourlyCampaignHistory: HourlyCampaignPoint[]
  campaignStats: Record<string, CampaignStats>
  projects: CampaignSummary[]
  sysInfo: SysInfo | null
  onSelectProject?: (id: string) => void
  onNavigate?: (route: string) => void
}

function fmtDuration(sec: number): string {
  if (!sec) return '—'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h > 0) return `${h}ч ${m}м`
  if (m > 0) return `${m}м`
  return `${sec}с`
}

// Idea #86 — короткое имя дня недели из YYYY-MM-DD (для оси графика пула).
function weekdayShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  if (isNaN(d.getTime())) return dateStr.slice(5)
  return ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'][d.getDay()] || dateStr.slice(5)
}

function TrendUp({ className = '' }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" />
    </svg>
  )
}

function TrendDown({ className = '' }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" /><polyline points="16 17 22 17 22 11" />
    </svg>
  )
}

function RingGauge({ label, value, max, detail, color }: {
  label: string; value: number; max: number; unit: string; detail: string; color: string
}) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  const hot = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : color
  return (
    <div className="flex items-center gap-3">
      {/* Tiny ring */}
      <div className="relative w-10 h-10 shrink-0">
        <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="color-mix(in oklab, currentColor 15%, transparent)" strokeWidth="3.5" className="text-muted-foreground" />
          <circle
            cx="20" cy="20" r="16"
            fill="none"
            stroke={hot}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * 2 * Math.PI * 16} ${2 * Math.PI * 16}`}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums" style={{ color: hot }}>
          {Math.round(pct)}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-1">
          <span className="text-xs font-medium text-foreground">{label}</span>
          <span className="text-[11px] tabular-nums font-semibold" style={{ color: hot }}>{Math.round(pct)}%</span>
        </div>
        <div className="text-[10px] tabular-nums text-muted-foreground mt-0.5">{detail}</div>
      </div>
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  // Enforce order: Успешно → Ошибки → Капча
  const order: Record<string, number> = { 'Успешно': 0, 'Ошибки': 1, 'Капча': 2, 'Пропуски': 3 }
  const sorted = [...payload].sort((a: any, b: any) => (order[a.name] ?? 99) - (order[b.name] ?? 99))
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg border"
      style={{ background: 'var(--popover)', borderColor: 'var(--border)', color: 'var(--popover-foreground)' }}
    >
      <p className="font-medium mb-1">{label}</p>
      {sorted.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          <span>{p.name}: <b className="tabular-nums">{p.value}</b></span>
        </div>
      ))}
    </div>
  )
}

function fmtHourLabel(hour: string): string {
  const parts = hour.split('T')
  if (parts.length !== 2) return hour
  const [date, h] = parts
  const [, m, d] = date.split('-')
  return `${d}.${m} ${h}:00`
}

function fmtDayLabel(day: string): string {
  const [, m, d] = day.split('-')
  const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
  const dt = new Date(day + 'T12:00:00')
  const wd = weekdays[dt.getDay()] || ''
  return `${wd} ${d}.${m}`
}

// ─── Yandex "weather" helpers ──────────────────────────────────────
type YandexDay = { date: string; top10_change: number; top50_change: number }
type WeatherResp = { days: YandexDay[]; custom_updates: Record<string, string> }
type WeatherLevel = 'calm' | 'elevated' | 'storm'
const weatherLevelOf = (v: number): WeatherLevel =>
  v >= 20 ? 'storm' : v >= 15 ? 'elevated' : 'calm'
const weatherLabel = (lvl: WeatherLevel) =>
  lvl === 'storm' ? 'Шторм в выдаче' : lvl === 'elevated' ? 'Колебания' : 'Спокойно'
const weatherHint = (lvl: WeatherLevel) =>
  lvl === 'storm'
    ? 'Выдача меняется заметно. Ожидайте больше капч и 429.'
    : lvl === 'elevated'
    ? 'Выдача немного колеблется. Небольшие скачки по позициям возможны.'
    : 'Алгоритмы Яндекса спокойны. Хороший день для работы.'
const weatherGradient = (lvl: WeatherLevel): string =>
  lvl === 'storm'
    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, transparent 60%)'
    : lvl === 'elevated'
    ? 'linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, transparent 60%)'
    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, transparent 60%)'
const weatherIconColor = (lvl: WeatherLevel) =>
  lvl === 'storm' ? '#ef4444' : lvl === 'elevated' ? '#eab308' : '#22c55e'
const WeatherIcon = ({ lvl, className = '' }: { lvl: WeatherLevel; className?: string }) =>
  lvl === 'storm' ? <CloudLightning className={className} strokeWidth={1.5} />
    : lvl === 'elevated' ? <CloudSun className={className} strokeWidth={1.5} />
    : <Sun className={className} strokeWidth={1.5} />

const BoosterOverview = React.memo(function BoosterOverview({
  stats, timeline, hourlyHistory, hourlyCampaignHistory, campaignStats, projects, sysInfo, onSelectProject, onNavigate,
}: Props) {
  const [period, setPeriod] = useState<'today' | 'week'>('today')
  const [weather, setWeather] = useState<WeatherResp | null>(null)
  const [topIdea, setTopIdea] = useState<Idea | null>(null)
  const [chartCampaignFilter, setChartCampaignFilter] = useState<string>('__all__')
  const [identStats, setIdentStats] = useState<IdentifierStats | null>(null)
  const [poolHistory, setPoolHistory] = useState<PoolHistory | null>(null)
  // Idea #74: server load history (CPU/RAM/load over time).
  const [loadHistory, setLoadHistory] = useState<Array<{ ts: number; cpu: number; ram: number; load1: number; workers: number }>>([])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await fetch('/api/yandex-updates?days=14')
        if (!r.ok) return
        const j = await r.json()
        if (!cancelled && j?.days) setWeather(j)
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await ideasList({ sort: 'top', limit: 5 })
        if (cancelled || !r?.ideas?.length) return
        const best = r.ideas
          .filter(i => !i.hidden && i.status !== 'rejected')
          .sort((a, b) => (b.score || 0) - (a.score || 0))[0]
        if (best) setTopIdea(best)
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Idea #40: показывать на рабочем столе AVG-сводку по идентификаторам.
  // Полезно когда у клиента несколько identifiers (cloud228, mybrand_2,
  // ...) и хочется быстро увидеть кто из них тащит / отстаёт.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await getIdentifierStats(period === 'today' ? 'today' : '7d')
        if (!cancelled) setIdentStats(r)
      } catch { /* ignore — endpoint may be unavailable on fresh install */ }
    })()
    return () => { cancelled = true }
  }, [period])

  // Idea #86: история числа профилей в облачных пулах по дням (снимок после крона).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const r = await getProfilePoolHistory(14)
        if (!cancelled) setPoolHistory(r)
      } catch { /* ignore — endpoint may be unavailable on fresh install */ }
    })()
    return () => { cancelled = true }
  }, [])

  // Idea #74: poll server load history for the «История нагрузки» chart.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const r = await fetch('/api/server-load-history?hours=24')
        if (!r.ok) return
        const j = await r.json()
        if (!cancelled && Array.isArray(j?.samples)) setLoadHistory(j.samples)
      } catch { /* ignore */ }
    }
    void load()
    const id = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const loadChartData = useMemo(() =>
    loadHistory.map(s => ({
      time: new Date(s.ts * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      'CPU %': Math.round(s.cpu),
      'RAM %': Math.round(s.ram),
      'Потоки': s.workers,
    })),
  [loadHistory])

  const speed = stats.booster_speed_per_hour ?? 0
  const avgPos = stats.booster_avg_position ?? 0
  const captchaTypes = stats.captcha_types ?? {}
  const speedPerDay = Math.round(speed * 24)

  const todayPrefix = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])

  const periodTotals = useMemo(() => {
    let ok = 0, fail = 0, captcha = 0, skip = 0
    const filtered = period === 'today'
      ? hourlyHistory.filter(h => h.hour.startsWith(todayPrefix))
      : hourlyHistory
    for (const h of filtered) { ok += h.ok; fail += h.fail; captcha += h.captcha }
    // Add live (not yet flushed) data from timeline. Idea #69: skip lives only
    // in the live timeline (not persisted hourly) — sum it from there.
    for (const p of timeline) {
      const d = new Date(p.ts * 1000)
      const hourKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}`
      if (period === 'today' && !hourKey.startsWith(todayPrefix)) continue
      skip += (p as any).skip || 0
      const alreadyCounted = filtered.some(h => h.hour === hourKey)
      if (!alreadyCounted) { ok += p.ok; fail += p.fail; captcha += p.captcha }
    }
    return { ok, fail, captcha, skip, total: ok + fail }
  }, [hourlyHistory, timeline, period, todayPrefix])

  const weekTotals = useMemo(() => {
    let ok = 0, fail = 0, captcha = 0
    for (const h of hourlyHistory) { ok += h.ok; fail += h.fail; captcha += h.captcha }
    return { ok, fail, captcha, total: ok + fail }
  }, [hourlyHistory])

  const { okCount, failCount, total, captchaTotal, skipTotal, successRate } = useMemo(() => {
    const ok = periodTotals.ok
    const fail = periodTotals.fail
    const t = ok + fail
    const cap = periodTotals.captcha
    const rate = t > 0 ? Math.round((ok / t) * 100) : 0
    return { okCount: ok, failCount: fail, total: t, captchaTotal: cap, skipTotal: periodTotals.skip, successRate: rate }
  }, [periodTotals])

  const chartData = useMemo(() => {
    const isWeek = period === 'week'
    const buckets = new Map<string, { ok: number; fail: number; captcha: number; skip: number; sortKey: string }>()

    // Idea #21: when a specific campaign is selected, use per-campaign
    // hourly history instead of the global one.
    const useCampaignFilter = chartCampaignFilter !== '__all__'
    const campaignHours = useCampaignFilter
      ? (hourlyCampaignHistory || []).filter((h: any) => h.cid === chartCampaignFilter)
      : []

    const sourceHours = useCampaignFilter ? campaignHours : hourlyHistory
    const filtered = period === 'today'
      ? sourceHours.filter((h: any) => h.hour.startsWith(todayPrefix))
      : sourceHours
    for (const h of filtered) {
      const bucketKey = isWeek ? h.hour.split('T')[0] : h.hour
      const b = buckets.get(bucketKey) || { ok: 0, fail: 0, captcha: 0, skip: 0, sortKey: bucketKey }
      b.ok += h.ok; b.fail += h.fail; b.captcha += h.captcha
      buckets.set(bucketKey, b)
    }
    if (!useCampaignFilter) {
      for (const p of timeline) {
        const d = new Date(p.ts * 1000)
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const hourKey = `${dateStr}T${String(d.getHours()).padStart(2, '0')}`
        if (period === 'today' && !hourKey.startsWith(todayPrefix)) continue
        const bucketKey = isWeek ? dateStr : hourKey
        const b = buckets.get(bucketKey) || { ok: 0, fail: 0, captcha: 0, skip: 0, sortKey: bucketKey }
        // Idea #69: skip comes only from the live timeline (not persisted hourly).
        b.ok += p.ok; b.fail += p.fail; b.captcha += p.captcha; b.skip += (p as any).skip || 0
        buckets.set(bucketKey, b)
      }
    }
    return Array.from(buckets)
      .sort((a, b) => a[1].sortKey.localeCompare(b[1].sortKey))
      .map(([key, b]) => ({ time: isWeek ? fmtDayLabel(key) : fmtHourLabel(key), 'Успешно': b.ok, 'Ошибки': b.fail, 'Капча': b.captcha, 'Пропуски': b.skip }))
  }, [timeline, hourlyHistory, hourlyCampaignHistory, chartCampaignFilter, period, todayPrefix])

  const captchaChartData = useMemo(() => {
    const LABELS: Record<string, string> = {
      'CAPTCHA: Обнаружена CheckBox': 'CheckBox',
      'CAPTCHA: Капча усложнена, SmartCaptcha IMG': 'SmartCaptcha',
      'CAPTCHA: Капча усложнена, TextCaptcha': 'TextCaptcha',
      'CAPTCHA: Капча усложнена, SliderCaptcha': 'SliderCaptcha',
      'CAPTCHA: Эскалация → SliderCaptcha': 'SliderCaptcha',
      'CAPTCHA: Эскалация → SmartCaptcha IMG': 'SmartCaptcha',
      'CAPTCHA: Эскалация → TextCaptcha': 'TextCaptcha',
      'Обнаружили CheckBox': 'CheckBox', 'Обнаружили Slider': 'SliderCaptcha',
      'Обнаружили TXT': 'TextCaptcha', 'Обнаружили SliderCaptcha': 'SliderCaptcha',
      'Яндекс Капча': 'SmartCaptcha',
    }
    const grouped: Record<string, number> = {}
    for (const [k, v] of Object.entries(captchaTypes)) {
      const label = LABELS[k] || k
      grouped[label] = (grouped[label] || 0) + (v as number)
    }
    return Object.entries(grouped).sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }))
  }, [captchaTypes])

  const activeProjects = useMemo(() => projects.filter(p => !p.archived), [projects])
  const runningProjects = useMemo(() => activeProjects.filter(p => p.running), [activeProjects])
  const stoppedProjects = useMemo(() => activeProjects.filter(p => !p.running), [activeProjects])
  const runningCount = runningProjects.length
  // Суммарно потоков по ЗАПУЩЕННЫМ проектам (CNT_THREADS из бэкенда).
  const runningThreads = useMemo(
    () => runningProjects.reduce((s, p) => s + (Number((p as { threads?: number }).threads) || 0), 0),
    [runningProjects],
  )

  // Idea #40: ТОП идентификаторов с AVG (avg = ok / unique_profiles_local).
  // Показываем секцию только если идентификаторов >= 2 (одиночному
  // пользователю это бесполезно).
  const identTopRows = useMemo(() => {
    if (!identStats?.groups?.length) return []
    const flat: Array<{
      ident: string; mode: string; ok: number; fail: number;
      profiles: number; avg: number;
    }> = []
    for (const g of identStats.groups) {
      for (const it of g.identifiers) {
        if (!it.identifier) continue
        const profiles = it.unique_profiles_local || 0
        const avg = profiles > 0 ? it.ok / profiles : 0
        flat.push({
          ident: it.identifier_label || it.identifier,
          mode: g.label || g.mode,
          ok: it.ok || 0,
          fail: it.fail || 0,
          profiles,
          avg,
        })
      }
    }
    flat.sort((a, b) => b.ok - a.ok)
    return flat.slice(0, 8)
  }, [identStats])

  // Compact KPI chips
  const chips: { icon: typeof Zap; label: string; value: string | number; hint?: string; color?: string }[] = [
    { icon: Activity, label: 'Выполнений', value: total.toLocaleString('ru'), hint: period === 'today' ? 'сегодня' : 'за 7 дней' },
    { icon: Zap, label: 'Скорость', value: speed > 0 ? `${Math.round(speed)}/ч` : '—', hint: speed > 0 ? `~${speedPerDay.toLocaleString('ru')}/сут` : '' },
    { icon: Target, label: 'Успешность', value: successRate > 0 ? `${successRate}%` : '—',
      hint: total > 0 ? `${okCount.toLocaleString('ru')} из ${total.toLocaleString('ru')}` : '',
      color: successRate >= 70 ? '#22c55e' : successRate >= 40 ? '#eab308' : '#ef4444' },
    { icon: ShieldAlert, label: 'Капча', value: captchaTotal.toLocaleString('ru'), hint: captchaTotal > 0 ? `${Object.keys(captchaTypes).length} типов` : '',
      color: captchaTotal > 0 ? '#a78bfa' : undefined },
    { icon: Target, label: 'AVG позиция', value: avgPos > 0 ? avgPos : '—', hint: avgPos > 0 ? 'без бренда' : '' },
  ]

  // Today's weather from Yandex feed
  const weatherToday = useMemo(() => {
    if (!weather?.days?.length) return null
    const last = weather.days[weather.days.length - 1]
    const lvl = weatherLevelOf(last.top10_change)
    const day = parseInt(last.date.split('-')[2], 10)
    const note = weather.custom_updates?.[String(day)] || null
    return { ...last, level: lvl, note }
  }, [weather])

  // Sparkline for hero today (last 24h)
  const sparkline = useMemo(() => {
    const recent = chartData.slice(-24)
    return recent.length ? recent.map((p: any, i: number) => ({ i, v: p['Успешно'] })) : []
  }, [chartData])

  // Per-type captcha colors — by meaning, not arbitrary
  const CAPTCHA_COLOR_MAP: Record<string, string> = {
    'CheckBox': '#22c55e',        // easy — green
    'SmartCaptcha': '#f59e0b',    // medium — amber
    'TextCaptcha': '#3b82f6',     // special — blue
    'SliderCaptcha': '#ef4444',   // hard — red
  }
  const CAPTCHA_FALLBACK = ['#64748b', '#475569', '#94a3b8'] // slate neutrals
  const getCaptchaColor = (name: string, idx: number) =>
    CAPTCHA_COLOR_MAP[name] || CAPTCHA_FALLBACK[idx % CAPTCHA_FALLBACK.length]

  return (
    <div className="p-4 md:p-6 space-y-5 animate-fade-in max-w-6xl">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-primary" />
            Рабочий стол
          </h1>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            {/* Running status pill */}
            <div className={`inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border text-[11px] ${
              runningCount > 0
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                : 'border-border bg-muted/40 text-muted-foreground'
            }`}>
              <span className="relative flex h-1.5 w-1.5">
                {runningCount > 0 && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                )}
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${runningCount > 0 ? 'bg-emerald-500' : 'bg-muted-foreground/60'}`} />
              </span>
              {runningCount > 0 ? (
                <span className="tabular-nums">
                  <span className="font-semibold">{runningCount}</span>
                  <span className="opacity-70"> / {activeProjects.length}</span>
                  <span className="ml-1 opacity-80">в работе</span>
                </span>
              ) : (
                <span>Проекты остановлены</span>
              )}
            </div>

            {runningThreads > 0 && (
              <div className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-border bg-muted/40 text-[11px] text-muted-foreground">
                <Cpu className="w-3 h-3" strokeWidth={2} />
                <span className="tabular-nums">
                  <span className="font-semibold text-foreground">{runningThreads.toLocaleString('ru')}</span>
                  <span className="ml-1">потоков</span>
                </span>
              </div>
            )}

            {weekTotals.total > 0 && (
              <div className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-border bg-muted/40 text-[11px] text-muted-foreground">
                <Activity className="w-3 h-3" strokeWidth={2} />
                <span className="tabular-nums">
                  <span className="font-semibold text-foreground">{weekTotals.total.toLocaleString('ru')}</span>
                  <span className="ml-1">за 7 дней</span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center rounded-lg border border-border p-0.5 bg-muted/50">
          <button
            onClick={() => setPeriod('today')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
              period === 'today' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Сегодня
          </button>
          <button
            onClick={() => setPeriod('week')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
              period === 'week' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Неделя
          </button>
        </div>
      </div>

      {/* ━━━━ KPI chips (top row) ━━━━ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {chips.map(c => {
          const Icon = c.icon
          return (
            <div key={c.label} className="rounded-xl border border-border bg-card px-4 py-3.5 flex items-center gap-3 transition-colors hover:bg-accent/30">
              <div className="w-11 h-11 rounded-xl bg-muted/60 flex items-center justify-center shrink-0 border border-border/50"
                style={c.color ? { color: c.color, background: `color-mix(in oklab, ${c.color} 12%, transparent)`, borderColor: `color-mix(in oklab, ${c.color} 25%, transparent)` } : undefined}>
                <Icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate font-medium">{c.label}</div>
                <div className="text-xl font-semibold tabular-nums tracking-tight text-foreground leading-tight mt-0.5"
                  style={c.color ? { color: c.color } : undefined}>
                  {c.value}
                </div>
                {c.hint && <div className="text-[10px] text-muted-foreground truncate mt-0.5">{c.hint}</div>}
              </div>
            </div>
          )
        })}
      </div>

      {/* ━━━━ Hero row: Today · Idea · Weather ━━━━ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Today main KPI */}
        <div className="rounded-2xl border border-border bg-card p-5 min-h-[160px] flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {period === 'today' ? 'Сегодня' : 'За 7 дней'}
              </div>
              {successRate > 0 && (
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 font-medium ${
                  successRate >= 70 ? 'border-emerald-500/30 text-emerald-500'
                    : successRate >= 40 ? 'border-yellow-500/30 text-yellow-500'
                    : 'border-red-500/30 text-red-500'
                }`}>
                  {successRate >= 50 ? <TrendUp className="mr-0.5" /> : <TrendDown className="mr-0.5" />}
                  {successRate}%
                </Badge>
              )}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">{total.toLocaleString('ru')}</div>
              <div className="text-sm text-muted-foreground">выполнений</div>
            </div>
            <div className="mt-2 flex gap-4 text-[11px]">
              <span className="text-emerald-500">
                <span className="tabular-nums font-semibold">{okCount.toLocaleString('ru')}</span>
                <span className="text-muted-foreground ml-1">успешно</span>
              </span>
              <span className="text-red-500">
                <span className="tabular-nums font-semibold">{failCount.toLocaleString('ru')}</span>
                <span className="text-muted-foreground ml-1">ошибок</span>
              </span>
              <span className="text-violet-400">
                <span className="tabular-nums font-semibold">{captchaTotal.toLocaleString('ru')}</span>
                <span className="text-muted-foreground ml-1">капч</span>
              </span>
            </div>
          </div>
          {sparkline.length > 1 && (
            <div className="mt-3 -mx-1">
              <ResponsiveContainer width="100%" height={34}>
                <LineChart data={sparkline} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                  <Line type="monotone" dataKey="v" stroke="#22c55e" strokeWidth={1.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Top idea */}
        <div className="rounded-2xl border border-border bg-card p-5 min-h-[160px] flex flex-col">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Lightbulb className="w-3 h-3 text-yellow-400" />
              <span>Идея дня</span>
            </div>
            {topIdea && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium border-emerald-500/30 text-emerald-500">
                <ThumbsUp className="w-2.5 h-2.5 mr-0.5" /> {topIdea.votes_up}
              </Badge>
            )}
          </div>
          {topIdea ? (
            <>
              <div className="mt-2 flex-1">
                <button
                  onClick={() => onNavigate?.({ idea: topIdea.id } as any)}
                  className="text-left w-full text-sm font-semibold text-foreground line-clamp-2 leading-snug hover:text-primary transition-colors"
                >{topIdea.title}</button>
                <p className="text-[11px] text-muted-foreground mt-1 line-clamp-3 leading-snug">{topIdea.description}</p>
              </div>
              <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-border/60">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground min-w-0">
                  <span className="truncate">{topIdea.author_label}</span>
                  <span className="w-1 h-1 rounded-full bg-muted-foreground/50 shrink-0" />
                  <span className="flex items-center gap-0.5 shrink-0"><MessageSquare className="w-2.5 h-2.5" />{topIdea.comments_count}</span>
                </div>
                <button
                  onClick={() => onNavigate?.({ idea: topIdea.id } as any)}
                  className="flex items-center gap-0.5 text-[10px] text-primary hover:underline shrink-0"
                >
                  открыть <ArrowRight className="w-2.5 h-2.5" />
                </button>
              </div>
            </>
          ) : (
            <div className="mt-2 flex-1 flex flex-col items-center justify-center text-center py-4">
              <Lightbulb className="w-6 h-6 text-muted-foreground/40 mb-1.5" />
              <p className="text-[11px] text-muted-foreground">Идей пока нет — предложите первую!</p>
              <button
                onClick={() => onNavigate?.('ideas')}
                className="mt-2 text-[11px] text-primary hover:underline flex items-center gap-0.5"
              >
                Перейти в раздел <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Weather (last, subdued) */}
        <div
          className="relative rounded-2xl border border-border bg-card p-5 overflow-hidden min-h-[160px] flex flex-col justify-between"
          style={weatherToday ? { backgroundImage: weatherGradient(weatherToday.level) } : undefined}
        >
          {weatherToday && (
            <div className="pointer-events-none absolute -right-8 -bottom-8 opacity-[0.04]" style={{ color: weatherIconColor(weatherToday.level) }}>
              <WeatherIcon lvl={weatherToday.level} className="w-36 h-36" />
            </div>
          )}
          <div className="relative">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span>Погода Яндекса</span>
              <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
              <span>сегодня</span>
            </div>
            {weatherToday ? (
              <>
                <div className="mt-2 flex items-center gap-2.5">
                  <div style={{ color: weatherIconColor(weatherToday.level) }} className="opacity-80">
                    <WeatherIcon lvl={weatherToday.level} className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-2xl font-semibold tabular-nums leading-none" style={{ color: weatherIconColor(weatherToday.level) }}>
                      {weatherToday.top10_change.toFixed(1)}%
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">изменение ТОП-10</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="text-sm font-medium text-foreground">{weatherLabel(weatherToday.level)}</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">{weatherHint(weatherToday.level)}</p>
                </div>
              </>
            ) : (
              <div className="mt-4 text-sm text-muted-foreground">Загрузка…</div>
            )}
          </div>
          {weather?.days && weather.days.length > 1 && (
            <div className="relative mt-3 -mx-1">
              <ResponsiveContainer width="100%" height={22}>
                <BarChart data={weather.days} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Bar dataKey="top10_change" radius={[1.5, 1.5, 0, 0]}>
                    {weather.days.map((d, i) => {
                      const lvl = weatherLevelOf(d.top10_change)
                      return <Cell key={i} fill={weatherIconColor(lvl)} fillOpacity={0.5} />
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-between text-[9px] text-muted-foreground/60 mt-0.5 px-1">
                <span>14 дней</span>
                <button onClick={() => onNavigate?.({ tool: 'yandex-updates' } as any)} className="hover:text-foreground transition-colors">подробнее →</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ━━━━ Charts row ━━━━ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main area chart */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card overflow-hidden">
          <div className="px-5 pt-5 pb-3 flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-emerald-500" />
                <h3 className="text-base font-semibold text-foreground">Выполнения</h3>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {period === 'today' ? 'Динамика за сегодня, по часам' : 'Динамика за 7 дней, по дням'}
              </p>
              {/* Idea #21: campaign filter dropdown */}
              {activeProjects.length > 1 && (
                <select
                  value={chartCampaignFilter}
                  onChange={e => setChartCampaignFilter(e.target.value)}
                  className="mt-1.5 text-[11px] bg-muted/40 border border-border/50 rounded-md px-2 py-1 text-foreground cursor-pointer max-w-[240px] truncate"
                >
                  <option value="__all__">Все проекты</option>
                  {activeProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              )}
            </div>
            {chartData.length > 0 && (
              <div className="flex items-center gap-1.5">
                {[
                  { k: 'Успешно', c: '#10B981', v: okCount },
                  { k: 'Ошибки', c: '#EF4444', v: failCount },
                  { k: 'Капча', c: '#a78bfa', v: captchaTotal },
                  ...(skipTotal > 0 ? [{ k: 'Пропуски', c: '#38bdf8', v: skipTotal }] : []),
                ].map(it => (
                  <div key={it.k} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/40 border border-border/50">
                    <span className="w-2 h-2 rounded-full" style={{ background: it.c }} />
                    <span className="text-[11px] text-muted-foreground">{it.k}</span>
                    <span className="text-[11px] tabular-nums font-semibold" style={{ color: it.c }}>{it.v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="px-2 pb-3">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gOk" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gFail" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#EF4444" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gCap" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.22} />
                      <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gSkip" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.20} />
                      <stop offset="95%" stopColor="#38bdf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.35} vertical={false} />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} minTickGap={24} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} allowDecimals={false} width={36} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '3 3' }} />
                  <Area type="monotone" dataKey="Капча" stroke="#a78bfa" fill="url(#gCap)" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="Пропуски" stroke="#38bdf8" fill="url(#gSkip)" strokeWidth={1.5} dot={false} strokeDasharray="2 2" />
                  <Area type="monotone" dataKey="Ошибки" stroke="#EF4444" fill="url(#gFail)" strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="Успешно" stroke="#10B981" fill="url(#gOk)" strokeWidth={2.25} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Activity className="w-8 h-8 opacity-30" />
                <p className="text-sm">
                  {total > 0 ? 'Сбор данных для графика…' : 'Данные появятся после запуска проекта'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-4">
          {/* Server Load — redesign */}
          {(() => {
            const cpu = sysInfo?.cpu_percent ?? 0
            const loadLevel: 'idle' | 'low' | 'mid' | 'high' | 'critical' =
              cpu >= 90 ? 'critical' : cpu >= 75 ? 'high' : cpu >= 50 ? 'mid' : cpu >= 20 ? 'low' : 'idle'
            const loadText =
              loadLevel === 'critical' ? 'Сервер перегружен — снизьте потоки немедленно'
              : loadLevel === 'high' ? 'Высокая нагрузка — добавлять потоки не стоит'
              : loadLevel === 'mid' ? 'Средняя нагрузка — можно немного увеличить'
              : loadLevel === 'low' ? 'Низкая нагрузка — есть запас для потоков'
              : 'Сервер простаивает — запускайте проекты'
            const loadColor =
              loadLevel === 'critical' ? '#ef4444'
              : loadLevel === 'high' ? '#f59e0b'
              : loadLevel === 'mid' ? '#eab308'
              : loadLevel === 'low' ? '#22c55e'
              : '#64748b'
            const isCritical = loadLevel === 'critical'
            return (
              <div className={`rounded-2xl border p-5 transition-all ${
                isCritical
                  ? 'border-red-500/50 bg-red-500/[0.04] shadow-[0_0_0_1px_rgba(239,68,68,0.15),0_0_24px_-6px_rgba(239,68,68,0.35)]'
                  : 'border-border bg-card'
              }`}>
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full" style={{ background: isCritical ? '#ef4444' : 'var(--primary)' }} />
                      <h3 className="text-base font-semibold text-foreground">Нагрузка сервера</h3>
                    </div>
                    {sysInfo?.cpu_model && (
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-snug">
                        {sysInfo.cpu_model.replace(/\(R\)|\(TM\)/g, '').trim()} · {sysInfo.cpu_count} ядер · {sysInfo.ram_total_gb} GB RAM
                      </p>
                    )}
                  </div>
                </div>
                {sysInfo ? (
                  <>
                    <div className="divide-y divide-border/50">
                      <div className="pb-3.5"><RingGauge label="CPU" value={sysInfo.cpu_percent} max={100} unit="%" detail={`${sysInfo.cpu_percent.toFixed(1)}%`} color="#3b82f6" /></div>
                      <div className="py-3.5"><RingGauge label="RAM" value={sysInfo.ram_used_gb} max={sysInfo.ram_total_gb} unit="GB" detail={`${sysInfo.ram_used_gb.toFixed(1)} / ${sysInfo.ram_total_gb.toFixed(1)} GB`} color="#8b5cf6" /></div>
                      <div className="pt-3.5"><RingGauge label="Диск" value={sysInfo.disk_used_gb} max={sysInfo.disk_total_gb} unit="GB" detail={`${sysInfo.disk_used_gb.toFixed(0)} / ${sysInfo.disk_total_gb.toFixed(0)} GB`} color="#06b6d4" /></div>
                    </div>
                    {/* Load verdict */}
                    <div className="mt-4 pt-3 border-t border-border/50 flex items-start gap-2">
                      <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ background: loadColor, boxShadow: isCritical ? `0 0 8px ${loadColor}` : undefined }} />
                      <p className="text-[11px] leading-snug" style={{ color: isCritical ? '#ef4444' : 'var(--muted-foreground)' }}>
                        <span className="font-medium" style={{ color: loadColor }}>CPU {Math.round(cpu)}%</span>
                        <span className="mx-1.5 text-muted-foreground/50">·</span>
                        <span className={isCritical ? 'font-medium text-red-500' : ''}>{loadText}</span>
                      </p>
                    </div>
                    {/* Idea #74: история нагрузки (CPU/RAM) за 24ч */}
                    {loadChartData.length > 1 && (
                      <div className="mt-4 pt-3 border-t border-border/50">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[11px] font-medium text-muted-foreground">История нагрузки (24ч)</span>
                          <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
                            <span className="flex items-center gap-1"><span className="w-2 h-0.5 rounded" style={{ background: '#3b82f6' }} />CPU</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-0.5 rounded" style={{ background: '#8b5cf6' }} />RAM</span>
                          </div>
                        </div>
                        <ResponsiveContainer width="100%" height={90}>
                          <LineChart data={loadChartData} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.3} vertical={false} />
                            <XAxis dataKey="time" tick={{ fontSize: 8, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} minTickGap={40} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 8, fill: 'var(--muted-foreground)' }} tickLine={false} axisLine={false} width={28} />
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'var(--border)', strokeWidth: 1, strokeDasharray: '3 3' }} />
                            <Line type="monotone" dataKey="CPU %" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                            <Line type="monotone" dataKey="RAM %" stroke="#8b5cf6" strokeWidth={1.5} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-center py-6 text-muted-foreground">Загрузка…</p>
                )}
              </div>
            )
          })()}

          {/* Captcha by Type — per-row colored by difficulty */}
          {captchaChartData.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
                    <h3 className="text-base font-semibold text-foreground">Капча по типам</h3>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {captchaTotal.toLocaleString('ru')} всего {period === 'today' ? 'сегодня' : 'за 7 дней'}
                  </p>
                </div>
              </div>
              <div className="divide-y divide-border/40">
                {captchaChartData.map((c, i) => {
                  const pct = captchaTotal > 0 ? (c.value / captchaTotal) * 100 : 0
                  const color = getCaptchaColor(c.name, i)
                  return (
                    <div key={c.name} className="py-2.5 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between text-[11px] mb-1.5">
                        <span className="flex items-center gap-2 text-foreground font-medium">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                          {c.name}
                        </span>
                        <span className="flex items-baseline gap-2">
                          <span className="tabular-nums font-semibold text-foreground">{c.value.toLocaleString('ru')}</span>
                          <span className="tabular-nums text-muted-foreground text-[10px] min-w-[32px] text-right">{pct.toFixed(0)}%</span>
                        </span>
                      </div>
                      <div className="h-1 rounded-full overflow-hidden bg-muted/40">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Running Projects Section */}
      {runningProjects.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-3">
            Активные проекты
            <span className="ml-2 text-sm font-normal text-muted-foreground">{runningProjects.length}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {runningProjects.map(p => {
              const cs = campaignStats[p.id]
              const pTotal = cs ? cs.ok + cs.fail : 0
              const pOk = cs?.ok ?? 0
              const pFail = cs?.fail ?? 0
              const pAvg = cs?.avg_position ?? 0
              const pCaptcha = cs?.captcha ?? 0
              const pRate = pTotal > 0 ? Math.round((pOk / pTotal) * 100) : 0

              return (
                <Card
                  key={p.id}
                  className="cursor-pointer transition-shadow hover:shadow-md"
                  style={{ borderLeftWidth: 3, borderLeftColor: p.color || '#10b981' }}
                  onClick={() => onSelectProject?.(p.id)}
                >
                  <CardHeader className="p-4 pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-sm truncate">{p.name}</CardTitle>
                      <Badge variant="default" className="text-xs shrink-0">Работает</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    {pTotal > 0 ? (
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { label: 'Всего', val: pTotal, cls: '' },
                          { label: 'Успех', val: `${pOk}`, cls: 'text-emerald-600 dark:text-emerald-400' },
                          { label: 'AVG', val: pAvg > 0 ? pAvg : '—', cls: '' },
                          { label: 'Капча', val: pCaptcha, cls: '' },
                        ].map(s => (
                          <div key={s.label} className="space-y-0.5">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</span>
                            <p className={`text-sm font-semibold tabular-nums ${s.cls}`}>{s.val}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {(p as any).workers_alive > 0
                          ? `В работе · ${(p as any).workers_alive}/${(p as any).workers_total ?? '?'} потоков`
                          : 'Запускается...'}
                      </p>
                    )}
                    {pTotal > 0 && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pRate}%` }} />
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">{pRate}%</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      {/* Idea #40: AVG по идентификаторам — компактная сводка */}
      {identTopRows.length >= 2 && (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              AVG по идентификаторам
              <span className="text-xs font-normal text-muted-foreground">
                ({period === 'today' ? 'сегодня' : 'за 7 дней'})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-muted-foreground border-y border-border">
                    <th className="text-left px-4 py-2">Идентификатор</th>
                    <th className="text-left px-4 py-2">Режим</th>
                    <th className="text-right px-4 py-2">OK</th>
                    <th className="text-right px-4 py-2">FAIL</th>
                    <th className="text-right px-4 py-2 whitespace-nowrap">Профилей</th>
                    <th className="text-right px-4 py-2 whitespace-nowrap" title="Среднее количество кликов на один профиль (OK / Профилей)">AVG/проф</th>
                  </tr>
                </thead>
                <tbody>
                  {identTopRows.map(r => (
                    <tr key={`${r.mode}|${r.ident}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{r.ident}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{r.mode}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">{r.ok.toLocaleString('ru-RU')}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-rose-600 dark:text-rose-400">{r.fail.toLocaleString('ru-RU')}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.profiles.toLocaleString('ru-RU')}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{r.avg > 0 ? r.avg.toFixed(2) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
              Сортировка по OK. Полная статистика — в разделе «Идентификаторы».
            </div>
          </CardContent>
        </Card>
      )}

      {/* Idea #86: профили в пуле по дням недели (снимок после ночного крона) */}
      {poolHistory?.pools?.length ? (
        <Card>
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              Профили в пуле по дням
              <span className="text-xs font-normal text-muted-foreground">
                (снимок после ночного крона, ~01:00 МСК)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-2 space-y-5">
            {poolHistory.pools.map(pool => {
              const data = pool.points.map(pt => ({
                day: weekdayShort(pt.date), date: pt.date, total: pt.total, free: pt.free,
              }))
              const up = pool.delta > 0, down = pool.delta < 0
              return (
                <div key={`${pool.mode}|${pool.identifier}`} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs truncate">{pool.identifier_label}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">{pool.mode}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">сейчас:</span>
                      <span className="font-semibold tabular-nums">{pool.latest.toLocaleString('ru-RU')}</span>
                      {(up || down) && (
                        <span className={`inline-flex items-center gap-0.5 ${up ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {up ? <TrendUp /> : <TrendDown />}
                          {Math.abs(pool.delta).toLocaleString('ru-RU')}
                        </span>
                      )}
                    </div>
                  </div>
                  {data.length >= 1 && (
                    <div className="h-28">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
                          <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                          <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 8 }}
                            formatter={(v: any, n: any) => [Number(v).toLocaleString('ru-RU'), n === 'total' ? 'Всего' : 'Свободно']}
                            labelFormatter={(l: any, p: any) => (p && p[0]?.payload?.date) || l}
                          />
                          <Bar dataKey="total" fill="#6366f1" radius={[3, 3, 0, 0]} maxBarSize={26} />
                          <Bar dataKey="free" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={26} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )
            })}
            <div className="text-xs text-muted-foreground">
              Синий — всего профилей в пуле, зелёный — свободных (used=0). Помогает поймать недостачу после крона.
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Stopped Projects Section */}
      <div>
        <h2 className="text-lg font-semibold tracking-tight mb-3">
          {runningProjects.length > 0 ? 'Остальные проекты' : 'Проекты'}
          <span className="ml-2 text-sm font-normal text-muted-foreground">{stoppedProjects.length}</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {stoppedProjects.map(p => (
              <Card
                key={p.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                style={{ borderLeftWidth: 3, borderLeftColor: p.color || 'var(--border)' }}
                onClick={() => onSelectProject?.(p.id)}
              >
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm truncate">{p.name}</CardTitle>
                    <Badge variant="secondary" className="text-xs shrink-0">Остановлен</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-xs text-muted-foreground">
                    {p.comment || 'Нажмите для настроек'}
                  </p>
                </CardContent>
              </Card>
          ))}

          {stoppedProjects.length === 0 && runningProjects.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <p className="text-sm text-muted-foreground">Создайте первый проект в боковой панели</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
})

export default BoosterOverview
